import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  // Optional pagination info for the bottom section
  paginationInfo?: {
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
  };
  // Optional condition for showing pagination info
  showPaginationInfo?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  loading = false,
  onPageChange,
  paginationInfo,
  showPaginationInfo = false,
}) => {
  if (totalPages <= 1) return null;

  // Function to generate page numbers with smart pagination
  const generatePageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisiblePages = 7; // Show maximum 7 page buttons

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination for many pages
      if (currentPage <= 4) {
        // Show first pages + ellipsis + last page
        for (let i = 1; i <= 5; i++) {
          // Adjusted to 5 to avoid too many pages
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first page + ellipsis + last pages
        pages.push(1);
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first + ellipsis + current range + ellipsis + last
        pages.push(1);
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div>
      <div className="flex justify-center mt-4 space-x-2">
        {/* Previous Button */}
        <button
          type="button"
          className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600 transition vertebra duration-200 transform hover:scale-105 disabled:hover:scale-100"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1 || loading}
        >
          {loading && currentPage > 1 ? (
            <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            '◀'
          )}
        </button>

        {/* Page Numbers */}
        {pageNumbers.map((page, idx) => {
          if (page === 'ellipsis') {
            return (
              <span key={`ellipsis-${idx}`} className="px-3 py-1 text-zinc-400 flexed items-center">
                ...
              </span>
            );
          }

          const pageNumber = page as number; // Safe type assertion since we checked for 'ellipsis'
          return (
            <button
              type="button"
              key={pageNumber}
              onClick={() => onPageChange(pageNumber)}
              disabled={loading}
              className={`px-3 py-1 rounded transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 ${
                currentPage === pageNumber
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
              } disabled:opacity-50 relative overflow-hidden`}
            >
              <span className={loading ? 'opacity-50' : ''}>{pageNumber}</span>
              {loading && currentPage === pageNumber && <div className="absolute inset-0 bg-red-600/20 animate-pulse"></div>}
            </button>
          );
        })}

        {/* Next Button */}
        <button
          type="button"
          className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages || loading}
        >
          {loading && currentPage < totalPages ? (
            <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            '▶'
          )}
        </button>
      </div>

      {/* Pagination Info Section
      {showPaginationInfo && paginationInfo && (
        <div className="flex justify-center mt-3">
          <div className="bg-zinc-800/50 px-4 py-2 rounded-lg backdrop-blur-sm border border-zinc-700/50">
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>Showing</span>
              <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded text-xs font-mono">
                {((paginationInfo.pageNumber - 1) * paginationInfo.pageSize) + 1}
              </span>
              <span>to</span>
              <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded text-xs font-mono">
                {Math.min(paginationInfo.pageNumber * paginationInfo.pageSize, paginationInfo.totalRecords)}
              </span>
              <span>of</span>
              <span className="px-2 py-1 bg-blue-900/30 text-blue-400 rounded text-xs font-mono">
                {paginationInfo.totalRecords}
              </span>
              <span>entries</span>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default Pagination;
